import type {
  Domain,
  HouseholdFactField,
  PersonFactField,
} from '@asvise/household-engine/vocabulary'

/**
 * Which questions each protection area is interested in.
 *
 * A PRESENTATION INDEX, NOT A RULE. The engine remains the only thing that
 * decides a status, and it already reports the household answers each finding's
 * reasoning read. This exists for the thing that report cannot do: offer an
 * area's questions before any of them are answered, and offer the questions
 * about a member.
 *
 * Both halves are needed, and the second is why this file is written by hand
 * rather than derived. A member's answers mostly reach the rules through the
 * derived household profile: a year of birth becomes an age and then a
 * vulnerability score, a yes to earning becomes an earner count and a dependant
 * count, cover through work becomes one `employment_cover` on the profile. By
 * the time a rule reads any of it, which member it came from and which domain
 * is reading it have both been averaged away. Deriving a per-area list from
 * that would say every area reads every member's answer, which is true and
 * useless.
 *
 * The commercial app has exactly this file and says the same thing about it.
 * What keeps it from drifting is not care, it is `areas.test.ts`, which asserts
 * this stays a superset of every answer the engine reports reading, across
 * households at several stages of being answered. An area that starts reading
 * something new and is not named here fails that test.
 *
 * WHAT IS DELIBERATELY ABSENT. Premiums, other spending, income stability, the
 * sustainable budget and a recent change belong to no area: they feed
 * affordability, urgency and the pathway rather than any one area's reasoning,
 * and putting them under an area would be the board claiming a connection the
 * engine does not make. They are asked in the record, in the framework's own
 * groups. Residency is absent for a stronger reason: nothing in the engine
 * reads it, because no public-scheme entitlement is inferred from where
 * somebody lives, and the demo scenario is explicit about that.
 */

export interface AreaInputs {
  household: HouseholdFactField[]
  /** Asked of the member the finding is about, where it is about one. */
  person: PersonFactField[]
}

/** A `Record`, so an area the engine screens and this file forgets fails the build. */
export const AREA_INPUTS: Record<Domain, AreaInputs> = {
  DEATH_INCOME_CONTINUITY: {
    /** The need's six, and the annual income the public benchmark multiplies. */
    household: [
      'survivor_expenses_monthly',
      'support_horizon_years',
      'total_debt',
      'education_commitments',
      'final_and_transition_costs',
      'savings_allocated_to_protection',
      'household_income_monthly',
    ],
    /**
     * Whether this is somebody the household depends on, which decides whether
     * the calculation describes them at all; whose income, which decides
     * whether the sole-earner rule fires; and cover through work, which is the
     * cover this area would otherwise count and then have to caveat.
     */
    person: [
      'earns',
      'income_share',
      'has_dependants',
      'birth_year',
      'employer_life_cover',
      'employer_cover_portable',
    ],
  },

  CRITICAL_ILLNESS_RECOVERY: {
    household: [
      'essential_expenses_monthly',
      'recovery_period_months',
      'recovery_additional_costs',
      'savings_allocated_to_protection',
      'household_income_monthly',
    ],
    person: ['earns', 'income_share', 'has_dependants', 'birth_year'],
  },

  DISABILITY_TPD: {
    /**
     * Nothing, and the engine says why on the finding itself: what a household
     * would spend on care and adaptation, and how long the income would have to
     * be replaced for, are not questions this question set asks. So this area
     * is judged on the cover found and on who it is about, and the honest offer
     * is the second of those rather than a list of figures that would not size
     * it.
     */
    household: [],
    person: ['earns', 'has_dependants', 'birth_year'],
  },

  HOSPITAL_SURGICAL: {
    /** What a stated bill would leave to find, against what could be reached. */
    household: ['liquid_savings', 'essential_expenses_monthly'],
    person: ['birth_year', 'employer_medical_cover', 'employer_cover_portable'],
  },

  EMERGENCY_LIQUIDITY: {
    household: ['liquid_savings', 'essential_expenses_monthly'],
    /** The household's buffer belongs to the household and to nobody in it. */
    person: [],
  },

  LONG_TERM_CARE: {
    /**
     * Screened by no rule in this set, so it produces no finding and this is
     * never read. Present because the type requires every area, and empty
     * rather than guessed: offering questions for an area nothing screens would
     * be collecting answers towards nothing.
     */
    household: [],
    person: [],
  },
}
